import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  api,
  getErrorMessage,
  type OrganizationUsage,
  type UsageDetail,
} from '../../api/client';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import { useAuth } from '../../context/AuthContext';
import NumericInput from '../../components/NumericInput';
import './BillingPage.css';

interface SubscriptionInfo {
  plan: string;
  status: string;
  billingInterval: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  expiresAt: string | null;
  isDemo: boolean;
  billingExempt: boolean;
  hasStripeSubscription: boolean;
  hasStripeCustomer: boolean;
  stripeConfigured: boolean;
}

interface InvoiceRow {
  _id: string;
  invoiceNumber: string | null;
  periodStart: string;
  periodEnd: string;
  currency: string;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  status: 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled';
  issuedAt: string | null;
  dueDate: string | null;
  paidAt: string | null;
}

const BillingPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  // Mirrors the server gate on PUT /api/billing/details: an org Admin with only
  // organization:read must not be handed a form whose save would 403.
  const canEditBillingDetails = hasPermission('organization:write');
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [usage, setUsage] = useState<OrganizationUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Spend-ceiling editor state. Stored as strings so the inputs can be cleared
  // without sticking at the minimum.
  const [memberLimit, setMemberLimit] = useState('100');
  const [activityLimit, setActivityLimit] = useState('3');
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsSaved, setLimitsSaved] = useState(false);

  // Platform invoices issued by Eventik to this org.
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [invoiceActionId, setInvoiceActionId] = useState<string | null>(null);
  // Distinct from "no invoices yet": the fetch itself failed (e.g. a 403 from
  // the permission gate). Collapsing the two hid a real error as an empty list.
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  // Invoicing identity, editable by the org itself: without it the monthly run
  // skips this org, so the page has to be able to fix that on its own.
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsSaved, setDetailsSaved] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [details, setDetails] = useState({
    legalName: '', ico: '', dic: '', street: '', city: '', zip: '', country: 'Česká republika',
    billingEmail: '', note: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Core billing data — a failure here is fatal for the page.
      const [subRes, usageRes] = await Promise.all([
        api.billing.getSubscription(),
        api.organization.getUsage(),
      ]);

      setSubscription(subRes.data);
      if (usageRes.data) {
        setUsage(usageRes.data);
        setMemberLimit(String(usageRes.data.entitlements.memberLimit));
        setActivityLimit(String(usageRes.data.entitlements.activityLimit));
      }

      // Invoices are supplementary — load best-effort so a failure here never
      // blanks the whole billing page (just shows an empty invoices section).
      try {
        const detailsRes = await api.billing.getDetails();
        const bd = detailsRes.data.billingDetails;
        setMissingFields(detailsRes.data.missingFields || []);
        if (bd) {
          setDetails({
            legalName: bd.legalName || '',
            ico: bd.ico || '',
            dic: bd.dic || '',
            street: bd.address?.street || '',
            city: bd.address?.city || '',
            zip: bd.address?.zip || '',
            country: bd.address?.country || 'Česká republika',
            billingEmail: bd.billingEmail || '',
            note: bd.note || '',
          });
        }
      } catch {
        // Non-fatal: the details section simply starts empty.
      }

      try {
        const invoicesRes = await api.billing.getInvoices();
        setInvoices(invoicesRes.data.invoices);
        setInvoicesError(null);
      } catch (invoicesErr) {
        setInvoices([]);
        setInvoicesError(getErrorMessage(invoicesErr));
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Live cost preview at the chosen ceiling — recomputed client-side with the
  // same formula the backend uses, so the admin sees the max they would pay.
  // Declared before any early return so this hook always runs (Rules of Hooks).
  const previewTotal = useMemo(() => {
    const pr = usage?.pricing;
    if (!pr) return null;
    const ml = Math.max(pr.includedMembers, Math.trunc(Number(memberLimit) || 0));
    const al = Math.max(pr.includedActivities, Math.trunc(Number(activityLimit) || 0));
    const memberBlocks = Math.ceil(Math.max(0, ml - pr.includedMembers) / pr.memberBlockSize);
    const extraActivities = Math.max(0, al - pr.includedActivities);
    return pr.basePrice
      + memberBlocks * pr.memberBlockPrice
      + extraActivities * pr.activityAddonPrice;
  }, [usage, memberLimit, activityLimit]);

  const handleSaveLimits = async () => {
    // Floor at the included base so we never submit below the minimum the
    // backend accepts. Source the floors from `pricing.*` so this stays in
    // step with the live cost preview.
    const minMembers = usage?.pricing.includedMembers ?? 100;
    const minActivities = usage?.pricing.includedActivities ?? 3;
    const ml = Math.max(minMembers, Math.trunc(Number(memberLimit) || 0));
    const al = Math.max(minActivities, Math.trunc(Number(activityLimit) || 0));
    try {
      setSavingLimits(true);
      setLimitsSaved(false);
      setError('');
      const res = await api.organization.updateLimits({ memberLimit: ml, activityLimit: al });
      setUsage(res.data);
      setMemberLimit(String(res.data.entitlements.memberLimit));
      setActivityLimit(String(res.data.entitlements.activityLimit));
      setLimitsSaved(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSavingLimits(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      setError('');
      const baseUrl = window.location.origin;
      const response = await api.billing.createPortalSession({
        returnUrl: `${baseUrl}/admin/billing`,
      });
      // Reset loading after timeout in case redirect is blocked
      setTimeout(() => setActionLoading(false), 5000);
      window.location.href = response.data.url;
    } catch (err) {
      setError(getErrorMessage(err));
      setActionLoading(false);
    }
  };

  // Fetch the invoice PDF as a blob, then either open it inline (view) or save
  // it (download). Reused for both the "current invoice" callout and history.
  const handleInvoicePdf = async (invoice: InvoiceRow, mode: 'view' | 'download') => {
    try {
      setInvoiceActionId(invoice._id);
      setError('');
      const res = await api.billing.downloadInvoicePdf(invoice._id);
      const blob = new Blob([res.data as BlobPart], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      if (mode === 'view') {
        window.open(url, '_blank', 'noopener');
        // Revoke later so the new tab has time to load it.
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `faktura-${invoice.invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setInvoiceActionId(null);
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('cs-CZ');
  };

  const formatStorage = (mb: number) => {
    if (mb >= 1000) return `${(mb / 1000).toFixed(mb % 1000 === 0 ? 0 : 1)} GB`;
    return `${Math.round(mb)} MB`;
  };

  const isOver = (detail: UsageDetail) => detail.limit !== -1 && detail.current > detail.limit;

  if (loading) {
    return (
      <div className="admin-form-page">
        <PageHeader title={t('billing.title')} helpKey="help.admin.billing" />
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (error && !subscription) {
    return (
      <div className="admin-form-page">
        <PageHeader title={t('billing.title')} helpKey="help.admin.billing" />
        <p className="error-message">{error}</p>
      </div>
    );
  }

  const pricing = usage?.pricing;
  const u = usage?.usage;
  const cost = usage?.cost;

  // Build the usage dimensions we display (members / activities / storage).
  const dimensions: Array<{ key: string; label: string; detail: UsageDetail; isStorage?: boolean }> = u
    ? [
        { key: 'members', label: t('billing.members'), detail: u.internalMembers },
        { key: 'activities', label: t('billing.activities'), detail: u.activities },
        { key: 'storage', label: t('billing.storage'), detail: u.storageMB, isStorage: true },
      ]
    : [];

  const hasOverage = dimensions.some((d) => isOver(d.detail));

  const saveDetails = async () => {
    setDetailsSaving(true);
    setDetailsError(null);
    setDetailsSaved(false);
    try {
      const res = await api.billing.updateDetails({
        legalName: details.legalName,
        ico: details.ico,
        ...(details.dic ? { dic: details.dic } : {}),
        address: {
          street: details.street,
          city: details.city,
          zip: details.zip,
          country: details.country,
        },
        billingEmail: details.billingEmail,
        ...(details.note ? { note: details.note } : {}),
      });
      setMissingFields(res.data.missingFields || []);
      setDetailsSaved(true);
    } catch (err) {
      setDetailsError(getErrorMessage(err));
    } finally {
      setDetailsSaving(false);
    }
  };

  const set = (field: keyof typeof details) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDetails((prev) => ({ ...prev, [field]: e.target.value }));

  // Feature the newest non-cancelled invoice as "current" (fall back to the
  // newest overall if every invoice is cancelled); the rest go to history.
  const currentInvoice = invoices.find((inv) => inv.status !== 'cancelled') ?? invoices[0];
  const historyInvoices = currentInvoice
    ? invoices.filter((inv) => inv._id !== currentInvoice._id)
    : [];

  return (
    <div className="admin-form-page billing-page">
      <PageHeader title={t('billing.title')} helpKey="help.admin.billing" />

      {error && <p className="error-message">{error}</p>}

      {/* Status Banners */}
      {subscription?.isDemo && subscription.expiresAt && (
        <div className="billing-banner billing-banner-warning">
          {(() => {
            const days = Math.max(0, Math.ceil((new Date(subscription.expiresAt).getTime() - Date.now()) / 86400000));
            return days > 0
              ? t('billing.demoExpiring', { days })
              : t('billing.demoBanner');
          })()}
        </div>
      )}

      {subscription?.isDemo && !subscription.expiresAt && (
        <div className="billing-banner billing-banner-info">
          {t('billing.demoBanner')}
        </div>
      )}

      {subscription?.status === 'expired' && (
        <div className="billing-banner billing-banner-error">
          {t('billing.expiredBanner')}
        </div>
      )}

      {subscription?.billingExempt && (
        <div className="billing-banner billing-banner-info">
          {t('billing.complimentary')}
        </div>
      )}

      {/* Cannot be invoiced yet — say so, and say what to do about it. */}
      {missingFields.length > 0 && !subscription?.isDemo && !subscription?.billingExempt && (
        <div className="billing-banner billing-banner-warning">
          {canEditBillingDetails ? t('billing.details.prompt') : t('billing.details.promptReadOnly')}
        </div>
      )}

      {/* Over-allowance warning */}
      {hasOverage && (
        <div className="billing-banner billing-banner-warning">
          {t('billing.overAllowance')}
        </div>
      )}

      {/* Usage Summary */}
      {u && (
        <div className="billing-section">
          <h3>{t('billing.usageSummary')}</h3>
          <div className="billing-usage-grid">
            {dimensions.map(({ key, label, detail, isStorage }) => {
              const over = isOver(detail);
              const currentLabel = isStorage ? formatStorage(detail.current) : String(detail.current);
              const limitLabel = detail.limit === -1
                ? t('billing.unlimited')
                : isStorage ? formatStorage(detail.limit) : String(detail.limit);
              const pct = detail.limit > 0 ? Math.min(100, (detail.current / detail.limit) * 100) : 0;
              return (
                <div className="billing-usage-item" key={key}>
                  <span className="billing-usage-label">{label}</span>
                  <span className={`billing-usage-value${over ? ' billing-usage-value-over' : ''}`}>
                    {currentLabel} {t('billing.of')} {limitLabel}
                  </span>
                  {detail.limit > 0 && (
                    <div className="billing-usage-bar">
                      <div
                        className={`billing-usage-fill${over ? ' billing-usage-fill-over' : ''}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Current monthly cost */}
      {cost && (
        <div className="billing-section">
          <h3>{t('billing.monthlyCost')}</h3>
          <div className="billing-cost-breakdown">
            <div className="billing-cost-row">
              <span className="billing-cost-label">{t('billing.basePrice')}</span>
              <span className="billing-cost-value">{formatPrice(cost.base, cost.currency)}</span>
            </div>
            <div className={`billing-cost-row${cost.memberCost === 0 ? ' billing-cost-row-muted' : ''}`}>
              <span className="billing-cost-label">
                {t('billing.costMembers', { blocks: cost.memberBlocks, size: pricing?.memberBlockSize ?? 100, price: cost.memberBlockPrice })}
              </span>
              <span className="billing-cost-value">{formatPrice(cost.memberCost, cost.currency)}</span>
            </div>
            <div className={`billing-cost-row${cost.activityCost === 0 ? ' billing-cost-row-muted' : ''}`}>
              <span className="billing-cost-label">
                {t('billing.costActivities', { count: cost.extraActivities, price: cost.activityAddonPrice })}
              </span>
              <span className="billing-cost-value">{formatPrice(cost.activityCost, cost.currency)}</span>
            </div>
            <div className="billing-cost-row billing-cost-row-total">
              <span className="billing-cost-label">{t('billing.total')}</span>
              <span className="billing-cost-value">
                {formatPrice(cost.total, cost.currency)} {t('billing.perMonth')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Spend-ceiling editor */}
      {pricing && (
        <div className="billing-section">
          <h3>{t('billing.setSpendLimit')}</h3>
          <p className="billing-manage-desc">{t('billing.setSpendLimitDesc')}</p>

          <div className="billing-usage-grid">
            <div className="billing-usage-item">
              <label className="billing-usage-label" htmlFor="memberLimit">{t('billing.memberLimit')}</label>
              <NumericInput
                id="memberLimit"
                min={pricing.includedMembers}
                step={pricing.memberBlockSize}
                value={memberLimit}
                readOnly
                onChange={(e) => { setMemberLimit(e.target.value); setLimitsSaved(false); }}
              />
            </div>
            <div className="billing-usage-item">
              <label className="billing-usage-label" htmlFor="activityLimit">{t('billing.activityLimit')}</label>
              <NumericInput
                id="activityLimit"
                min={pricing.includedActivities}
                step={1}
                value={activityLimit}
                readOnly
                onChange={(e) => { setActivityLimit(e.target.value); setLimitsSaved(false); }}
              />
            </div>
          </div>

          {previewTotal !== null && (
            <p className="billing-limit-preview">
              {t('billing.maxAtThisLimit', { price: formatPrice(previewTotal, pricing.currency) })}
            </p>
          )}

          <div className="billing-limit-actions">
            <Button onClick={handleSaveLimits} loading={savingLimits}>
              {savingLimits ? t('common.saving') : t('common.save')}
            </Button>
            {limitsSaved && <span className="billing-limit-saved">{t('common.saved')}</span>}
          </div>
        </div>
      )}

      {/* Platform invoices issued by Eventik */}
      <div className="billing-section" id="billing-details">
        <h3>{t('billing.details.title')}</h3>
        <p className="billing-manage-desc">{t('billing.details.help')}</p>

        {detailsError && <p className="error-message">{detailsError}</p>}

        <div className="form-grid">
          <div className="form-grid--full">
            <FormField label={t('billing.details.legalName')} htmlFor="bd-legalName" required>
              <input id="bd-legalName" type="text" readOnly={!canEditBillingDetails} className="form-control" value={details.legalName} onChange={set('legalName')} />
            </FormField>
          </div>
          <FormField label={t('billing.details.ico')} htmlFor="bd-ico" required>
            <input id="bd-ico" type="text" readOnly={!canEditBillingDetails} className="form-control" value={details.ico} onChange={set('ico')} />
          </FormField>
          <FormField label={t('billing.details.dic')} htmlFor="bd-dic">
            <input id="bd-dic" type="text" readOnly={!canEditBillingDetails} className="form-control" value={details.dic} onChange={set('dic')} />
          </FormField>
          <FormField label={t('billing.details.street')} htmlFor="bd-street" required>
            <input id="bd-street" type="text" readOnly={!canEditBillingDetails} className="form-control" value={details.street} onChange={set('street')} />
          </FormField>
          <FormField label={t('billing.details.city')} htmlFor="bd-city" required>
            <input id="bd-city" type="text" readOnly={!canEditBillingDetails} className="form-control" value={details.city} onChange={set('city')} />
          </FormField>
          <FormField label={t('billing.details.zip')} htmlFor="bd-zip" required>
            <input id="bd-zip" type="text" readOnly={!canEditBillingDetails} className="form-control" value={details.zip} onChange={set('zip')} />
          </FormField>
          <FormField label={t('billing.details.country')} htmlFor="bd-country" required>
            <input id="bd-country" type="text" readOnly={!canEditBillingDetails} className="form-control" value={details.country} onChange={set('country')} />
          </FormField>
          <div className="form-grid--full">
            <FormField label={t('billing.details.billingEmail')} htmlFor="bd-email" required>
              <input id="bd-email" type="email" readOnly={!canEditBillingDetails} className="form-control" value={details.billingEmail} onChange={set('billingEmail')} />
            </FormField>
          </div>
          <div className="form-grid--full">
            <FormField label={t('billing.details.note')} htmlFor="bd-note">
              <textarea id="bd-note" className="form-control" rows={2} value={details.note} onChange={set('note')} />
            </FormField>
          </div>
        </div>

        {canEditBillingDetails ? (
          <div className="billing-details-actions">
            <Button onClick={saveDetails} loading={detailsSaving}>
              {detailsSaving ? t('common.saving') : t('billing.details.save')}
            </Button>
            {detailsSaved && missingFields.length === 0 && (
              <span className="billing-manage-desc">{t('billing.details.saved')}</span>
            )}
          </div>
        ) : (
          <p className="billing-manage-desc">{t('billing.details.noPermission')}</p>
        )}
      </div>

      <div className="billing-section">
        <h3>{t('billing.invoices.title')}</h3>
        {invoicesError ? (
          <p className="error-message">{invoicesError}</p>
        ) : invoices.length === 0 ? (
          <p className="billing-manage-desc">{t('billing.invoices.empty')}</p>
        ) : (
          <>
            {/* Current (most recent non-cancelled) invoice */}
            <div className="billing-invoice-current">
              <div className="billing-invoice-current-head">
                <span className="billing-invoice-current-label">{t('billing.invoices.current')}</span>
                <span className={`badge badge-status-${currentInvoice.status}`}>
                  {t(`billing.invoices.status.${currentInvoice.status}`)}
                </span>
              </div>
              <div className="billing-invoice-current-amount">
                {formatPrice(Number(currentInvoice.grossAmount), currentInvoice.currency)}
              </div>
              <div className="billing-invoice-current-meta">
                {currentInvoice.invoiceNumber && (
                  <span>{t('billing.invoices.number')}: {currentInvoice.invoiceNumber}</span>
                )}
                <span>{formatDate(currentInvoice.periodStart)} – {formatDate(currentInvoice.periodEnd)}</span>
                {currentInvoice.dueDate && (
                  <span>{t('billing.invoices.due')}: {formatDate(currentInvoice.dueDate)}</span>
                )}
              </div>
              {currentInvoice.status === 'draft' ? (
                <p className="billing-manage-desc">
                  {t('billing.invoices.draftNote')}{' '}
                  {missingFields.length > 0 && (
                    <a href="#billing-details">{t('billing.invoices.fillDetails')}</a>
                  )}
                </p>
              ) : (
              <div className="billing-invoice-actions">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleInvoicePdf(currentInvoice, 'view')}
                  loading={invoiceActionId === currentInvoice._id}
                >
                  {t('billing.invoices.view')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleInvoicePdf(currentInvoice, 'download')}
                  loading={invoiceActionId === currentInvoice._id}
                >
                  {t('billing.invoices.download')}
                </Button>
              </div>
              )}
            </div>

            {/* History (all other invoices) */}
            {historyInvoices.length > 0 && (
              <div className="billing-invoice-history">
                <h4 className="billing-invoice-history-title">{t('billing.invoices.history')}</h4>
                <div className="billing-invoice-list">
                  {historyInvoices.map((inv) => (
                    <div className="billing-invoice-row" key={inv._id}>
                      <span className="billing-invoice-row-number">{inv.invoiceNumber ?? '–'}</span>
                      <span className="billing-invoice-row-period">
                        {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                      </span>
                      <span className="billing-invoice-row-amount">
                        {formatPrice(Number(inv.grossAmount), inv.currency)}
                      </span>
                      <span className={`badge badge-status-${inv.status}`}>
                        {t(`billing.invoices.status.${inv.status}`)}
                      </span>
                      <span className="billing-invoice-row-actions">
                        {inv.invoiceNumber && (<>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleInvoicePdf(inv, 'view')}
                          loading={invoiceActionId === inv._id}
                        >
                          {t('billing.invoices.view')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleInvoicePdf(inv, 'download')}
                          loading={invoiceActionId === inv._id}
                        >
                          {t('billing.invoices.download')}
                        </Button>
                        </>)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Manage Subscription (legacy Stripe customers only) */}
      {subscription?.hasStripeCustomer && subscription.stripeConfigured && (
        <div className="billing-section">
          <h3>{t('billing.manageSubscription')}</h3>
          <p className="billing-manage-desc">{t('billing.manageDescription')}</p>
          <button
            className="btn btn--secondary"
            onClick={handleManageSubscription}
            disabled={actionLoading}
          >
            {actionLoading ? t('common.loading') : t('billing.openPortal')}
          </button>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
